import React from "react";
import {
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import LoadingModal from "components/common/loading/LoadingModal";
import ConfirmDeleteModal from "components/Modal/ConfirmDeleteModal/ConfirmDeleteModal";

import ManufacturerAccountModal from "./modal/ManufacturerAccountModal";
import useManufacturerAccount from "./useManufacturerAccount";

import { Delete, Edit, Password } from "@mui/icons-material";
import CustomTable from "components/common/table/CustomTable";
import { MRT_GlobalFilterTextField as GlobalFilterTextField } from "material-react-table";
import { textFilter } from "utils/table";

const ManufacturerAccounts = () => {
  const {
    isFetching,
    isDeleting,
    manufacturersAccounts,
    showChangePasswordModal,
    selectedManufacturerAccount,
    showManufacturerAccountAddModal,
    showManufacturerAccountDeleteModal,

    setManufacturerAccount,
    fetchManufacturerAccounts,
    setShowChangePasswordModal,
    deleteSelectedManufacturer,
    handleDeactivateScheduling,
    handleDeactivateManufacturer,
    setShowManufacturerAccountAddModal,
    getDeleteManufacturerAccountBodyText,
    setShowManufacturerAccountDeleteModal,
  } = useManufacturerAccount();

  const columnData = [
    {
      header: "Manufacturer Name",
      accessorKey: "manufacturer",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Account",
      accessorKey: "account",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "User Group",
      accessorKey: "usergroup",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "User Name",
      accessorKey: "username",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Active",
      accessorKey: "isActive",
      filterVariant: "boolean",
      size: 60,
      accessorFn: (rowData) => {
        return (
          <FormControlLabel
            style={{ textAlign: "left", justifyContent: "flex-start" }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              handleDeactivateManufacturer(
                rowData._id,
                e.target.checked,
                rowData.isActive
              );
            }}
            control={
              <Switch name="isActive" defaultChecked={rowData.isActive} />
            }
          />
        );
      },
    },
    {
      header: "Scheduling",
      accessorKey: "schedule",
      filterVariant: "boolean",
      Cell: ({ row }) => {
        const rowData = row?.original;
        return (
          <FormControlLabel
            style={{ textAlign: "left", justifyContent: "flex-start" }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              handleDeactivateScheduling(
                rowData._id,
                e.target.checked,
                rowData?.schedule
              );
            }}
            control={
              <Switch name="schedule" defaultChecked={rowData.schedule} />
            }
          />
        );
      },
    },
    {
      header: "Actions",
      accessor: "actions",
      filterVariant: "string",
      enableSorting: false,
      size: 100,
      accessorFn: (rowData) => {
        return (
          <div>
            <Tooltip title={"Edit"}>
              <Edit
                className="action-buttons__edit"
                onClick={() => {
                  setManufacturerAccount(rowData);
                  setShowManufacturerAccountAddModal(true);
                }}
              />
            </Tooltip>

            <Tooltip title={"Delete"}>
              <Delete
                className="action-buttons__delete ml-2"
                onClick={() => {
                  setManufacturerAccount(rowData);
                  setShowManufacturerAccountDeleteModal(true);
                }}
              />
            </Tooltip>

            <Tooltip title={"Change password"}>
              <Password
                className="action-buttons__edit ml-2"
                onClick={() => {
                  setManufacturerAccount(rowData);
                  setShowChangePasswordModal(true);
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const tableComponents = {
    FilterRow: (props) => {
      return (
        <tr className="trans-table__filterRow">
          <td>
            <div style={{ margin: "10px 0", padding: "0 10px 0px" }}>
              <TextField
                onChange={(event) => {
                  props.onFilterChanged(0, event.target.value);
                }}
              />
            </div>
          </td>
          <td>
            <div style={{ margin: "10px 0", padding: "0 10px 0px" }}>
              <TextField
                onChange={(event) => {
                  props.onFilterChanged(1, event.target.value);
                }}
              />
            </div>
          </td>
          <td>
            <div style={{ margin: "10px 0", padding: "0 10px 0px" }}>
              <TextField
                onChange={(event) => {
                  props.onFilterChanged(2, event.target.value);
                }}
              />
            </div>
          </td>
          <td>
            <div style={{ margin: "10px 0", padding: "0 10px 0px" }}>
              <TextField
                onChange={(event) => {
                  props.onFilterChanged(3, event.target.value);
                }}
              />
            </div>
          </td>
        </tr>
      );
    },
  };

  return (
    <div>
      <div id="userTable">
        <div className="trans-table__container">
          <CustomTable
            data={manufacturersAccounts}
            columns={columnData}
            components={tableComponents}
            options={{
              enableTopToolbar: true,
              enableSorting: true,
              state: {
                isLoading: isFetching,
                showGlobalFilter: true,
              },
              renderTopToolbar: ({ table }) => (
                <div
                  className="d-flex"
                  style={{
                    padding: "10px",
                    borderBottom: "1px solid #e0e0e0",
                    gap: "10px",
                  }}
                >
                  <GlobalFilterTextField table={table} />
                  <Button
                    onClick={() => setShowManufacturerAccountAddModal(true)}
                    size="small"
                    color="secondary"
                    variant="contained"
                  >
                    + Add Manufacturer Configuration
                  </Button>
                </div>
              ),
            }}
          />
        </div>
      </div>

      {showManufacturerAccountAddModal && (
        <ManufacturerAccountModal
          isModalOpen={showManufacturerAccountAddModal}
          selectedManufacturerAccount={selectedManufacturerAccount}
          isUpdate={!!selectedManufacturerAccount}
          onClose={(reason) => {
            setShowManufacturerAccountAddModal(false);
            setManufacturerAccount();
            if (reason === "success") {
              fetchManufacturerAccounts();
            }
          }}
        />
      )}

      {showManufacturerAccountDeleteModal && (
        <ConfirmDeleteModal
          isLoading={false}
          isModalOpen={showManufacturerAccountDeleteModal}
          modalBodyText={getDeleteManufacturerAccountBodyText()}
          onClose={() => setShowManufacturerAccountDeleteModal(false)}
          onDelete={() =>
            deleteSelectedManufacturer(selectedManufacturerAccount._id)
          }
        />
      )}

      {showChangePasswordModal && (
        <ManufacturerAccountModal
          isChangePassword={true}
          isModalOpen={showChangePasswordModal}
          selectedManufacturerAccount={selectedManufacturerAccount}
          onClose={() => setShowChangePasswordModal(false)}
          fetchManufacturerAccounts={fetchManufacturerAccounts}
        />
      )}

      {isDeleting && (
        <LoadingModal loadingText="Deleting Manufacturer Account..." />
      )}
    </div>
  );
};

export default ManufacturerAccounts;
